import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { getQcsSyncResources, qcsSyncUpdateResource } from 'data/qcsSyncResourceSagaHelpers';
import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';

import { getLogger } from 'utils/logger';
import AxiosFactory from 'utils/axiosFactory';
import { Config, ENVIRONMENT } from 'utils/webappConfig';

import { updatePayeeListSuccess } from 'data/payees/actions';
import { getPayeesForAccounts } from 'data/payees/selectors';
import { isUncategorizedTxn, isSplitTxn, isTransferTxn } from 'data/transactions/utils';
import { getFeatureFlags } from 'utils/featureFlags/selectors';
import { preProcessUpdate } from 'data/transactions/preProcess';
import { isLoanAccountFromId } from 'data/accounts/utils';
import { getAccountsById } from 'data/accounts/selectors';
import { shouldPostponeAction } from 'data/postponedActions/postponedActionsHelpers';
import { addPostponedAction, dismissPostponedActions } from 'data/postponedActions/postponedActionsActions';
import { clearUndoStack } from 'data/undo/undoActions';
import { coasAreEqual } from 'data/chartOfAccounts/utils';

import store from 'store';

import * as actions from './actions';
import { getLastSyncDate, getTransactionsByAccountIdAndTxnId,
  getTransactionsByAccountId, getTransactionsById } from './selectors';

import { assignTxnIds, markTxnsDeleted } from './sagasUtil';

import {
  transformTransactionToRequestData,
  transformResponseToTransactions } from './transformers';

const log = getLogger('transactions/sagas');

const qcsAxios = AxiosFactory.get('qcs');

// ===============================================================================================
// SAGAS functions for transactions.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.  Data is returned via ACTIONS, however, these can be
// split out (and should be) to be fully functional and JS shareable API callers via Axios and
// simply return an object in a provided callback, which can then choose to call the actions
//


// ====================================================
// CREATE TRANSACTIONS
//
// batch create the array of provided transactions
// uses optimistic loading of REDUX with restore
// transfer handling to be done by caller before
// making this call
//
export function* createTransactions(action) {
  const { payload: txnsToCreate } = action;

  // Optimistically save the transactions in REDUX
  // gives immediate feedback
  const txnsToRestore = assignTxnIds(txnsToCreate);
  log.debug('Creating transactions...', txnsToRestore);

  const preActionPayload = {
    resources: txnsToRestore,
    replace: true,
  };
  yield put(actions.createTransactionsSuccessOptimistic(preActionPayload, action.meta));

  try {

    const requestPayload = { resources: [] };
    txnsToCreate.forEach((txn) => {
      requestPayload.resources.push(transformTransactionToRequestData(txn));
    });
    // log.debug('Request payload for creating transactions', requestPayload);

    const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/transactions`;

    // using BATCH MODE so we can create more than 1 txn if needed
    const response = yield call(qcsAxios.put, requestUrl, requestPayload);
    // log.debug('Received response when creating transaction', response);

    // since in batch mode, we must check and process results of each item
    // we need to match it up the the clientId

    const replaceRecord = replaceOptimisticTransactions(response.data.items, txnsToCreate, 'clientId');

    // if there was an error code returned, fail
    if (replaceRecord.errorCount > 0) {
      throw new Error('Whoops, an error occurred trying to create transactions');
    }
    const txnsForRedux = replaceRecord.txns;
    const txnsToDelete = markTxnsDeleted(txnsToRestore);

    const actionPayload = {
      resources: txnsForRedux.concat(txnsToDelete),
      replace: true,
    };
    yield put(actions.createTransactionsSuccess(actionPayload, action.meta));

  } catch (error) {
    log.error('CREATE Transaction Error...', error);

    //
    // Delete the transactions we optimistically put in REDUX above (id=0)
    //
    const txnsToDelete = markTxnsDeleted(txnsToRestore);
    const postActionPayload = {
      resources: txnsToDelete,
      replace: true,
    };
    yield put(actions.deleteTransactionsSuccessOptimistic(postActionPayload));

    if (shouldPostponeAction(action, error)) {
      yield put(dismissPostponedActions());
      yield put(clearUndoStack());
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(actions.createTransactionsFailure(error, action.meta)) }));
    } else {
      yield put(actions.createTransactionsFailure(error, action.meta));
    }
  }
}

function correlateSplitIds(txn, correlationIds) {
  // let's look for split id matches in the correlation id's
  let retTxn = txn;
  if (correlationIds && isSplitTxn(txn)) {
    retTxn = txn.setIn(['split','items'], txn.split.items.map((splitItem) => {
      const matchingId = correlationIds[splitItem.clientId];
      return matchingId ? splitItem.merge({ id: matchingId, clientId: null }) : splitItem
    }))
  }
  return retTxn;
}

function replaceOptimisticTransactions(items, txnsSent, findIdProperty, onlyUpdateChanged = false) {

  const updatedTxns = [];
  const txnsToDelete = [];
  let errorCount = 0;

  items.forEach((item) => {
    if (item.code < 300) {
      const thisTxn = txnsSent.find((txn) => txn.get(findIdProperty) === item.resource[findIdProperty]);

      const updatedTxn = correlateSplitIds(thisTxn, item.resource.correlationIds);

      if (!onlyUpdateChanged || (updatedTxn !== thisTxn)) {
        updatedTxns.push(updatedTxn.merge({
          id: onlyUpdateChanged ? updatedTxn.id : item.resource.id,  // only change id's when creating
          clientId: null,
        }));
      }
    } else {
      log.log(`There was a problem creating a transaction ${item.code}`);
      errorCount += 1;
    }
  });

  //
  // Delete the transactions we optimistically put in REDUX
  //
  return { txns: updatedTxns.concat(txnsToDelete), errorCount };
}

// ====================================================
// UPDATE TRANSACTIONS
//
// Optimistic update only if a single tx given
//
export function* updateTransactions(action) {
  const { payload: txnsToUpdate } = action;
  log.debug('Updating transaction...', txnsToUpdate, action.meta);

  const batch = false; // turned on optimistic update for multiple txns txnsToUpdate.length > 1;
  let oldTxns;

  if (!batch) {

    try {
      // save the current version in REDUX for revert if our
      // optimistic save must be undone
      oldTxns = yield select(
        getTransactionsByAccountIdAndTxnId, txnsToUpdate
      );

      // WE optimistically write this transaction to REDUX and we undo it
      // if the save fails

      if (oldTxns) {
        let optimisticTxnList = txnsToUpdate;
        //
        // if the account changed, then since the tx store is "by account" the reducer will not change/remove
        // the old transaction (in the different account store), so we delete it in REDUX
        //
        oldTxns.forEach((txn) => {
          if (txn.accountId !== txnsToUpdate.find((x) => x.id === txn.id).accountId) {
            optimisticTxnList = [].concat(txnsToUpdate);  // copy
            optimisticTxnList.push(txn.set('isDeleted', true));
          }
        });
        const preActionPayload = {
          resources: optimisticTxnList,
          replace: true,
        };
        yield put(actions.updateTransactionsSuccessOptimistic(preActionPayload, action.meta));
      }
    } catch (e) {
      log.error(e);
    }
  }

  try {

    const requestData = { resources: txnsToUpdate.map((txn) => transformTransactionToRequestData(txn)) };
    const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/transactions`;

    // log.debug('Url = ', requestUrl);
    // log.debug('data = ', requestData);

    const response = yield call(qcsAxios.put, requestUrl, requestData);

    const replaceRecord = replaceOptimisticTransactions(response.data.items, txnsToUpdate, 'id', true);
    const txnsForRedux = replaceRecord.txns;

    const actionPayload = {
      resources: txnsForRedux,
      replace: true,
    };

    yield put(actions.updateTransactionsSuccess(actionPayload, action.meta));

    if (replaceRecord.errorCount > 0) {
      throw new Error('Whoops, an error occurred trying to update one or more of your transactions');
    }
    // TODO We are NOT handling responses from QCS properly, in particular, when a error is returned
    // TODO in the 200 payload or in a batch update when not all transactions succeeded
    //
    log.debug('UPDATING TRANSACTION SUCCESS');

  } catch (error) {
    log.error('UPDATE Transaction Error...', error);

    // Restore the REDUX store
    if (!batch && oldTxns) {
      const postActionPayload = {

        resources: oldTxns,
        replace: true,
      };
      yield put(actions.updateTransactionsSuccessOptimistic(postActionPayload, action.meta));
    }

    if (shouldPostponeAction(action, error)) {
      yield put(dismissPostponedActions());
      yield put(clearUndoStack());
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(actions.updateTransactionsFailure(error, action.meta)) }));
    } else {
      yield put(actions.updateTransactionsFailure(error, action.meta));
    }

  }
}

// ====================================================
// DELETE TRANSACTIONS
//
// Actually uses PUT (update) and sets the isDeleted flag.
// Note, no transfer handling, must be done by caller
// use utilities in sagasUtils
//
export function* deleteTransactions(action) {
  const { payload: allTxnsToDelete } = action;
  // log.debug('In SAGAS: Delete Transaction', allTxnsToDelete);

  const preActionPayload = {

    resources: allTxnsToDelete,
    replace: true,
  };
  yield put(actions.deleteTransactionsSuccessOptimistic(preActionPayload, action.meta));

  const requestUrl = `${Config.environments[ENVIRONMENT].services_url}/transactions`;
  try {

    const requestData = { resources: allTxnsToDelete.map((txn) => transformTransactionToRequestData(txn)) };
    yield call(qcsAxios.put, requestUrl, requestData);

    yield put(actions.deleteTransactionsSuccess(undefined, action.meta));

  } catch (error) {
    log.error('DELETE Transaction Error...', error);
    if (shouldPostponeAction(action, error)) {
      yield put(addPostponedAction({ action, error, onFailure: () => store.dispatch(actions.deleteTransactionsFailure(error, action.meta)) }));
    } else {
      yield put(actions.deleteTransactionsFailure(error, action.meta));
    }

    const postActionPayload = {

      resources: markTxnsDeleted(allTxnsToDelete, false),
      replace: true,
    };
    yield put(actions.updateTransactionsSuccessOptimistic(postActionPayload, action.meta));
  }
}

// ====================================================
// AUTOCATEGORIZE TRANSACTIONS
//
// reviews recently retrieved transactions and looks for uncategorized transactions that have a match in the
// payee list, and will automatically categorize using the provided suggestion
//
export function* autoCategorizeTransactions(action) {

  const { payload: txnsToCheck } = action;
  const payeeList = yield select(getPayeesForAccounts, { accountIds: null});

  // log.debug('Autocategorizing ', txnsToCheck);

  let txnsToUpdate = txnsToCheck.map((txn) =>
    (isUncategorizedTxn(txn) && txn.payee && payeeList.get(txn.payee.toLowerCase())) ? txn : null );

  txnsToUpdate = txnsToUpdate.filter((txn) => (txn !== null && !isLoanAccountFromId(txn.accountId)));
  txnsToUpdate = txnsToUpdate.map((txn) => {
    const payeeEntry = payeeList.get(txn.payee.toLowerCase());
    if (payeeEntry && !isSplitTxn(payeeEntry.txn) && !isTransferTxn(payeeEntry.txn) ) {
      return txn.set('coa', payeeEntry.txn.coa).set('split', payeeEntry.txn.split);
    }
    return null;
  });
  txnsToUpdate = txnsToUpdate.filter((txn) => txn !== null);

  const txnsByAccountId = yield select(getTransactionsByAccountId);
  const featureFlags = yield select(getFeatureFlags);

  log.debug('Auto categorizing transactions pre ', txnsToUpdate);

  const txnPack = preProcessUpdate({
    txns: txnsToUpdate,
    txnsByAccountId,
    featureFlags,
  });

  log.debug('Auto categorizing transactions ', txnPack);

  if (txnPack.allTxnsToUpdate.length > 0) {
    yield put(actions.updateTransactions(txnPack.allTxnsToUpdate));
  }
}

// ====================================================
//
// UPDATE TRANSACTION CATEGORIES FOR PAYEE
//
// reviews recently retrieved transactions and looks for uncategorized transactions that have a match in the
// payee list, and will automatically categorize using the provided suggestion
//
export function* updateTransactionCategoriesForPayee(action) {

  const { coa, payee, priorCoa } = action.payload;
  const transactionsById = yield select(getTransactionsById, {});

  // console.log("UPDATE CATS ", payee, priorCoa, coa);
  let txnsToUpdate = transactionsById.filter((txn) => (txn.payee === payee) && coasAreEqual(txn.coa, priorCoa));
  txnsToUpdate = txnsToUpdate.map((txn) => txn.set('coa', coa));

  const txnsByAccountId = yield select(getTransactionsByAccountId);
  const featureFlags = yield select(getFeatureFlags);

  const txnPack = preProcessUpdate({
    txns: txnsToUpdate,
    txnsByAccountId,
    featureFlags,
  });

  log.debug('Updating Transaction Categories by Payee ', txnPack);

  if (txnPack.allTxnsToUpdate.length > 0) {
    yield put(actions.updateTransactions(txnPack.allTxnsToUpdate, { context: 'register', undo: { userMessage: 'Transaction(s) updated.' } }));
  }
}

// ====================================================
// CREATE OPENING BALANCE TRANSACTIONS
//
// takes an array of newly created accounts, and based on the balanceAsOf values
// it will create an opening balance transaction on that day specified
//
export function* createOpeningBalanceTransaction(action) {

  const { newTxn, accountClientId } = action.payload;
  if (!newTxn || !accountClientId) {
    return;
  }

  const accountsById = yield select(getAccountsById);
  const account = accountsById.find((x) => x.clientId === accountClientId);

  if (account) {
    const newTxnWithAccount = newTxn.set('accountId', account.id);
    yield put(actions.createTransactions([newTxnWithAccount], action.meta));
  }
}


//---------------------------------------------------
let lastGetTransactionsPayloads = [];

export function* storePayload(data) {
  log.debug('Store Payload', data);
  lastGetTransactionsPayloads = lastGetTransactionsPayloads.concat(data.payload.resources);
}

export function* triggerAutoCategorize() {
  if (lastGetTransactionsPayloads.length > 0) {
    yield put(actions.autoCategorizeTransactions(lastGetTransactionsPayloads));
    lastGetTransactionsPayloads = [];
  }
}
//---------------------------------------------------


// ====================================================
// PERFORM TRANSACTION ACTION
// a saga for utilizing the transactions/{id}/action or transactions/{id}/action/{otherId}
// pass in an action api object
//
// {
//   id: txnId to impact
//   action: 'ACCEPT' || 'SKIP' || 'MATCH' || 'UNMATCH'   See QCS Documentation for updates
//   otherId: if two IDs are involved in action (e.g. match)
// }
//
export function* performTransactionAction(actionData) {
  const { payload: actionObject } = actionData;
  const { action, id, otherId, ...other } = actionObject;
  const config = mkQcsSyncResourceConfig({
    resourceName: 'transaction action',
    resourceBaseUrl: `/transactions/${id}/${action.toLowerCase()}${otherId ? `/${otherId}` : ''}`,
    successAction: actions.performTransactionActionSuccess,
    failureAction: actions.performTransactionActionFailure,
    getLastSyncDate: () => null,
  });
  const actionConverted = { ...actionData, payload: { ...other } };
  yield call(qcsSyncUpdateResource, config, actionConverted);
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'transaction',
  resourceBaseUrl: '/transactions',
  getLastSyncDate,

  transformResponseToResources: transformResponseToTransactions,

  successAction,
  failureAction,
});

export function* getTransactionsActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getTransactions);

    const config = mkResourceConfig(actions.getTransactionsSuccess, actions.getTransactionsFailure);
    yield call(getQcsSyncResources, config, action);

  }
}

export function* createTransactionsActionWatcher() {
  yield takeEvery(actions.createTransactions, createTransactions);
}

export function* updateTransactionsActionWatcher() {
  yield takeEvery(actions.updateTransactions, updateTransactions);
}

export function* deleteTransactionsActionWatcher() {
  yield takeEvery(actions.deleteTransactions, deleteTransactions);
}

export function* performTransactionActionWatcher() {
  yield takeEvery(actions.performTransactionAction, performTransactionAction);
}

export function* getTransactionsSuccessActionWatcher() {
  yield takeLatest([
    actions.getTransactionsSuccess,
  ], storePayload);
}
export function* updatePayeeListSuccessActionWatcher() {
  yield takeLatest([
    updatePayeeListSuccess,
  ], triggerAutoCategorize);
}

export function* autoCategorizeTransactionsActionWatcher() {
  yield takeLatest(actions.autoCategorizeTransactions, autoCategorizeTransactions);
}

export function* updateTransactionCategoriesForPayeeActionWatcher() {
  yield takeLatest(actions.updateTransactionCategoriesForPayeeAction, updateTransactionCategoriesForPayee);
}

export function* createOpeningBalanceTransactionActionWatcher() {
  yield takeLatest(actions.createOpeningBalanceTransactionAction, createOpeningBalanceTransaction);
}

// ====================================================
// EXPORTS

export default [
  getTransactionsActionWatcher,
  createTransactionsActionWatcher,
  updateTransactionsActionWatcher,
  deleteTransactionsActionWatcher,
  autoCategorizeTransactionsActionWatcher,
  performTransactionActionWatcher,
  updatePayeeListSuccessActionWatcher,
  createOpeningBalanceTransactionActionWatcher,
  updateTransactionCategoriesForPayeeActionWatcher,
  getTransactionsSuccessActionWatcher];
